import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import {
  RefreshCw, DollarSign, Clock, Truck, Package,
  CheckCircle, XCircle, AlertTriangle, Search, Eye
} from 'lucide-react';
import { getImageUrl } from '@/utils/helpers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  pending: { color: 'bg-yellow-500', icon: Clock, label: 'Pending' },
  paid: { color: 'bg-blue-500', icon: DollarSign, label: 'Paid' },
  artist_shipped: { color: 'bg-cyan-500', icon: Truck, label: 'Artist Shipped' },
  platform_received: { color: 'bg-teal-500', icon: Package, label: 'Platform Received' },
  shipped: { color: 'bg-indigo-500', icon: Truck, label: 'Shipped to Buyer' },
  delivered: { color: 'bg-emerald-500', icon: CheckCircle, label: 'Delivered' },
  completed: { color: 'bg-green-500', icon: CheckCircle, label: 'Completed' },
  cancelled: { color: 'bg-red-500', icon: XCircle, label: 'Cancelled' },
  refunded: { color: 'bg-gray-500', icon: RefreshCw, label: 'Refunded' },
};

const AdminArtworkPurchases = ({ token: propToken }) => {
  const { token: authToken } = useAuth();
  const token = propToken || authToken;

  const [orders, setOrders] = useState([]);
  const [orphanedArtworks, setOrphanedArtworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [showOrphaned, setShowOrphaned] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [ordersRes, orphanedRes] = await Promise.all([
        axios.get(`${API}/artwork-purchase/admin/orders`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/artwork-purchase/admin/orphaned-sold`, { headers }).catch(() => ({ data: [] })),
      ]);
      setOrders(ordersRes.data || []);
      setOrphanedArtworks(orphanedRes.data || []);
    } catch (err) {
      console.error('Failed to fetch artwork purchases:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const updateStatus = async (orderId, newStatus) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/artwork-purchase/order/${orderId}/status`, { status: newStatus }, { headers });
      toast.success(`Status updated to ${newStatus}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    }
  };

  const fixOrphanedArtwork = async (artworkId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/artwork-purchase/admin/fix-orphaned`, { artwork_id: artworkId }, { headers });
      toast.success('Artwork sold status has been reset');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fix failed');
    }
  };

  const getStatusBadge = (status) => {
    const cfg = STATUS_CONFIG[status] || { color: 'bg-gray-500', icon: Clock, label: status };
    const Icon = cfg.icon;
    return (
      <Badge className={`${cfg.color} text-white flex items-center gap-1 w-fit`}>
        <Icon className="w-3 h-3" /> {cfg.label}
      </Badge>
    );
  };

  const filteredOrders = orders.filter(order => {
    if (statusFilter !== 'all' && order.status !== statusFilter) return false;
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      return (
        order.artwork_title?.toLowerCase().includes(s) ||
        order.buyer_name?.toLowerCase().includes(s) ||
        order.id?.toLowerCase().includes(s)
      );
    }
    return true;
  });

  const stats = {
    total: orders.length,
    pending: orders.filter(o => o.status === 'pending').length,
    paid: orders.filter(o => o.status === 'paid').length,
    shipped: orders.filter(o => ['artist_shipped', 'platform_received', 'shipped'].includes(o.status)).length,
    completed: orders.filter(o => ['delivered', 'completed'].includes(o.status)).length,
    totalRevenue: orders.filter(o => o.payment_status === 'paid').reduce((s, o) => s + (o.price || 0), 0),
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div data-testid="admin-artwork-purchases">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Artwork Purchases</h1>
        <Button onClick={fetchData} variant="outline" size="sm">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Data Integrity Alert */}
      {orphanedArtworks.length > 0 && (
        <Card className="mb-6 border-amber-300 bg-amber-50" data-testid="orphaned-alert">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="font-bold text-amber-900">Data Integrity Issue Found</h3>
                <p className="text-sm text-amber-700 mt-1">
                  {orphanedArtworks.length} artwork(s) marked as "sold" but have no corresponding purchase order record.
                  This may indicate a payment processing issue or manual data change.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3 border-amber-400 text-amber-800"
                  onClick={() => setShowOrphaned(!showOrphaned)}
                  data-testid="toggle-orphaned-btn"
                >
                  {showOrphaned ? 'Hide Details' : 'View Affected Artworks'}
                </Button>
                {showOrphaned && (
                  <div className="mt-3 space-y-2">
                    {orphanedArtworks.map(art => (
                      <div key={art.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-amber-200">
                        <div className="flex items-center gap-3">
                          {art.image_url && (
                            <img src={getImageUrl(art.image_url)} alt="" className="w-10 h-10 rounded object-cover" />
                          )}
                          <div>
                            <p className="font-medium text-sm">{art.title}</p>
                            <p className="text-xs text-gray-500">ID: {art.id}</p>
                          </div>
                        </div>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => fixOrphanedArtwork(art.id)}
                          data-testid={`fix-orphaned-${art.id}`}
                        >
                          Reset Sold Status
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
        <Card className="bg-gray-50"><CardContent className="p-4 text-center"><p className="text-2xl font-bold">{stats.total}</p><p className="text-sm text-gray-500">Total</p></CardContent></Card>
        <Card className="bg-yellow-50"><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-yellow-600">{stats.pending}</p><p className="text-sm text-yellow-600">Pending</p></CardContent></Card>
        <Card className="bg-blue-50"><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-blue-600">{stats.paid}</p><p className="text-sm text-blue-600">Paid</p></CardContent></Card>
        <Card className="bg-indigo-50"><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-indigo-600">{stats.shipped}</p><p className="text-sm text-indigo-600">In Transit</p></CardContent></Card>
        <Card className="bg-green-50"><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-green-600">{stats.completed}</p><p className="text-sm text-green-600">Completed</p></CardContent></Card>
        <Card className="bg-amber-50"><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-amber-600">{stats.totalRevenue.toLocaleString()}</p><p className="text-sm text-amber-600">Revenue (kWh)</p></CardContent></Card>
      </div>

      {/* Filters */}
      <div className="flex gap-4 mb-4">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search artwork, buyer..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-artwork-purchases"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40" data-testid="status-filter">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="artist_shipped">Artist Shipped</SelectItem>
            <SelectItem value="platform_received">Platform Received</SelectItem>
            <SelectItem value="shipped">Shipped</SelectItem>
            <SelectItem value="delivered">Delivered</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Orders Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Artwork</TableHead>
                <TableHead>Buyer</TableHead>
                <TableHead>Price (kWh)</TableHead>
                <TableHead>Artist Revenue</TableHead>
                <TableHead>Payment</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredOrders.map((order) => (
                <TableRow key={order.id} data-testid={`artwork-order-${order.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      {order.artwork_image && (
                        <img src={getImageUrl(order.artwork_image)} alt="" className="w-12 h-12 rounded object-cover" />
                      )}
                      <span className="font-medium text-sm max-w-[150px] truncate">{order.artwork_title}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">{order.buyer_name || '-'}</TableCell>
                  <TableCell className="font-medium">{order.price?.toLocaleString()}</TableCell>
                  <TableCell className="text-sm text-green-600">{order.artist_revenue?.toLocaleString()} ({order.artist_revenue_percent}%)</TableCell>
                  <TableCell><Badge variant="outline">{order.payment_method || '-'}</Badge></TableCell>
                  <TableCell>{getStatusBadge(order.status)}</TableCell>
                  <TableCell className="text-sm text-gray-500">{order.created_at ? new Date(order.created_at).toLocaleDateString() : '-'}</TableCell>
                  <TableCell>
                    <Button variant="ghost" size="sm" onClick={() => { setSelectedOrder(order); setShowDetail(true); }} data-testid={`view-order-${order.id}`}>
                      <Eye className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {filteredOrders.length === 0 && (
            <div className="text-center py-12 text-gray-500" data-testid="no-artwork-purchases">
              No artwork purchases found
            </div>
          )}
        </CardContent>
      </Card>

      {/* Order Detail Dialog */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Artwork Purchase Details</DialogTitle>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-4">
              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                {selectedOrder.artwork_image && (
                  <img src={getImageUrl(selectedOrder.artwork_image)} alt="" className="w-20 h-20 rounded object-cover" />
                )}
                <div>
                  <p className="font-bold">{selectedOrder.artwork_title}</p>
                  <p className="text-sm text-gray-500">Order: {selectedOrder.id?.slice(0, 8)}...</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><p className="text-gray-500">Status</p>{getStatusBadge(selectedOrder.status)}</div>
                <div><p className="text-gray-500">Payment</p><p className="font-medium">{selectedOrder.payment_method}</p></div>
                <div><p className="text-gray-500">Price</p><p className="font-medium">{selectedOrder.price?.toLocaleString()} kWh</p></div>
                <div><p className="text-gray-500">Artist Revenue</p><p className="font-medium text-green-600">{selectedOrder.artist_revenue?.toLocaleString()} kWh</p></div>
                <div><p className="text-gray-500">Buyer</p><p>{selectedOrder.buyer_name || selectedOrder.buyer_id}</p></div>
                <div><p className="text-gray-500">Date</p><p>{selectedOrder.created_at ? new Date(selectedOrder.created_at).toLocaleString() : '-'}</p></div>
                {selectedOrder.tracking_number && (
                  <div className="col-span-2"><p className="text-gray-500">Tracking</p><p>{selectedOrder.tracking_number}</p></div>
                )}
              </div>

              {/* Status Update */}
              {selectedOrder.status !== 'completed' && selectedOrder.status !== 'cancelled' && (
                <div className="pt-4 border-t">
                  <p className="text-sm font-medium mb-2">Update Status</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedOrder.status === 'paid' && (
                      <Button size="sm" onClick={() => { updateStatus(selectedOrder.id, 'artist_shipped'); setShowDetail(false); }}>Mark: Artist Shipped</Button>
                    )}
                    {selectedOrder.status === 'artist_shipped' && (
                      <Button size="sm" onClick={() => { updateStatus(selectedOrder.id, 'platform_received'); setShowDetail(false); }}>Mark: Platform Received</Button>
                    )}
                    {selectedOrder.status === 'platform_received' && (
                      <Button size="sm" onClick={() => { updateStatus(selectedOrder.id, 'shipped'); setShowDetail(false); }}>Mark: Shipped to Buyer</Button>
                    )}
                    {selectedOrder.status === 'shipped' && (
                      <Button size="sm" onClick={() => { updateStatus(selectedOrder.id, 'delivered'); setShowDetail(false); }}>Mark: Delivered</Button>
                    )}
                    {selectedOrder.status === 'delivered' && (
                      <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => { updateStatus(selectedOrder.id, 'completed'); setShowDetail(false); }}>Mark: Completed</Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetail(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminArtworkPurchases;
